/**
 * BookingPipeline Component
 * Visual representation of a booking's lifecycle journey
 * Shows current stage, progress, and allows manual stage overrides
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/admin/ui/card'
import { Badge } from '@/components/admin/ui/badge'
import { Button } from '@/components/admin/ui/button'
import {
  LIFECYCLE_STAGES,
  STAGE_LABELS,
  STAGE_COLORS,
  STAGE_ORDER,
  getStageIndex,
  type LifecycleStage
} from '@/lib/booking-lifecycle'
import { CheckCircle2, Circle, Edit, AlertCircle } from 'lucide-react'
import { StageOverrideModal } from './StageOverrideModal'

interface BookingPipelineProps {
  bookingId: string
  currentStage: LifecycleStage
  stageUpdatedAt?: string
  stageUpdatedBy?: string
  stageOverrideReason?: string
  hasMediaIssue?: boolean
  mediaUploadProgress?: { completed: number; total: number }
  talentPaymentStatus?: {
    photographerAssigned?: boolean
    photographerPaid?: boolean | null
    videographerAssigned?: boolean
    videographerPaid?: boolean | null
    secondPhotographerAssigned?: boolean
    secondPhotographerPaid?: boolean | null
  }
  onStageUpdate?: () => void
}

export function BookingPipeline({
  bookingId,
  currentStage,
  stageUpdatedAt,
  stageUpdatedBy,
  stageOverrideReason,
  hasMediaIssue = false,
  mediaUploadProgress,
  talentPaymentStatus,
  onStageUpdate
}: BookingPipelineProps) {
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const currentIndex = getStageIndex(currentStage)

  const hasTalentPaymentData = talentPaymentStatus !== undefined
  const isTalentPaidStageComplete = (() => {
    if (!talentPaymentStatus) return true

    const assignedRoles = [
      {
        assigned: talentPaymentStatus.photographerAssigned,
        paid: talentPaymentStatus.photographerPaid,
      },
      {
        assigned: talentPaymentStatus.videographerAssigned,
        paid: talentPaymentStatus.videographerPaid,
      },
      {
        assigned: talentPaymentStatus.secondPhotographerAssigned,
        paid: talentPaymentStatus.secondPhotographerPaid,
      },
    ].filter(role => role.assigned)

    return assignedRoles.length > 0 && assignedRoles.every(role => role.paid === true)
  })()

  const getStageStatus = (stageIndex: number) => {
    const stage = STAGE_ORDER[stageIndex]
    if (
      hasTalentPaymentData &&
      stage === LIFECYCLE_STAGES.TALENT_PAID &&
      !isTalentPaidStageComplete
    ) {
      return 'upcoming'
    }

    if (stageIndex < currentIndex) return 'completed'
    if (stageIndex === currentIndex) return 'current'
    return 'upcoming'
  }

  return (
    <>
      <Card className="border-2">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Booking Lifecycle</CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowOverrideModal(true)}
              className="gap-2"
            >
              <Edit className="h-4 w-4" />
              Update Stage
            </Button>
          </div>

          {stageOverrideReason && (
            <div className="mt-2 text-xs text-muted-foreground bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded p-2">
              <AlertCircle className="h-3 w-3 inline mr-1" />
              Override: {stageOverrideReason}
            </div>
          )}
        </CardHeader>

        <CardContent>
          {/* Horizontal Pipeline */}
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-muted">
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${(currentIndex / (STAGE_ORDER.length - 1)) * 100}%` }}
              />
            </div>

            {/* Stage Dots */}
            <div className="relative flex justify-between items-start gap-1">
              {STAGE_ORDER.map((stage, index) => {
                const status = getStageStatus(index)
                const isMediaIssueStage = stage === LIFECYCLE_STAGES.MEDIA_ISSUE
                const showMediaProgress = stage === LIFECYCLE_STAGES.MEDIA_UPLOADED && mediaUploadProgress

                return (
                  <div
                    key={stage}
                    className="flex flex-col items-center flex-1 min-w-0"
                  >
                    {/* Stage Dot */}
                    <div className="relative z-10 mb-2">
                      {status === 'completed' ? (
                        <CheckCircle2 className="h-5 w-5 text-primary fill-primary" />
                      ) : status === 'current' ? (
                        <div className={`h-5 w-5 rounded-full border-4 border-primary ${
                          isMediaIssueStage && hasMediaIssue
                            ? 'bg-red-500 border-red-500'
                            : 'bg-background'
                        } ring-4 ring-primary/20`} />
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>

                    {/* Stage Label */}
                    <Badge
                      variant={status === 'current' ? 'default' : 'outline'}
                      className={`
                        text-[10px] leading-tight text-center py-1 px-1.5 h-auto max-w-full
                        ${status === 'current' ? STAGE_COLORS[stage] : 'bg-muted/50'}
                        ${status === 'current' && isMediaIssueStage && hasMediaIssue ? 'bg-red-500 text-white' : ''}
                      `}
                    >
                      <span className="break-words hyphens-auto" style={{ wordBreak: 'break-word' }}>
                        {STAGE_LABELS[stage]}
                      </span>
                    </Badge>

                    {/* Media Upload Progress */}
                    {showMediaProgress && status === 'current' && mediaUploadProgress.total > 0 && (
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        {mediaUploadProgress.completed}/{mediaUploadProgress.total}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Current Stage Info */}
          <div className="mt-6 pt-4 border-t">
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className="text-muted-foreground">Current Stage: </span>
                <span className="font-medium">{STAGE_LABELS[currentStage]}</span>
              </div>
              {stageUpdatedAt && (
                <span className="text-xs text-muted-foreground">
                  Updated {new Date(stageUpdatedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <StageOverrideModal
        open={showOverrideModal}
        onOpenChange={setShowOverrideModal}
        bookingId={bookingId}
        currentStage={currentStage}
        onSuccess={() => {
          setShowOverrideModal(false)
          onStageUpdate?.()
        }}
      />
    </>
  )
}
